<script setup lang="ts">
import { computed } from "vue";
import { UiDialog, UiText, UiChip, UiInfoBox, UiStandardButton, UiTooltip } from "@shopexperts/shared";
import type { AiSummaryKind, AiSummaryStep, ExpertAiStepRun } from "@shopexperts/legacy-api";
import {
  AI_RUN_TRIGGER_LABELS,
  AI_SUMMARY_STEP_LABELS,
  formatAiCost,
  formatAiDateTime,
  formatAiDuration,
  formatAiModel,
} from "@/app/composables/expert-manager/aiSummaries";
import { useExpertAiStepRuns } from "@/app/composables/expert-manager/useExpertAiSummaries";
import AiRunStatusChip from "./AiRunStatusChip.vue";

const props = defineProps<{
  expertId: number;
  step: AiSummaryStep | null;
  currentRunId?: number | null;
  isPublishing?: boolean;
}>();

const emit = defineEmits<{
  (e: "view-run", runId: number): void;
  (e: "publish", payload: { kind: AiSummaryKind; runId: number }): void;
}>();

const open = defineModel<boolean>({ default: false });

const step = computed(() => props.step);
const { runs, total, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useExpertAiStepRuns(
  () => props.expertId,
  { step, enabled: computed(() => open.value && step.value !== null) },
);

const summaryKind = computed<AiSummaryKind | null>(() => {
  if (props.step === "profile_summary") return "profile";
  if (props.step === "reviews_summary") return "reviews";
  return null;
});

const title = computed(() => (props.step ? `${AI_SUMMARY_STEP_LABELS[props.step]} revisions` : "Revisions"));
const subtitle = computed(() =>
  summaryKind.value
    ? "Newest first. Any succeeded revision can be made live again without regenerating."
    : "Newest first. Open a run to inspect what the model received and returned.",
);

function meta(run: ExpertAiStepRun): string {
  return [
    AI_RUN_TRIGGER_LABELS[run.trigger],
    formatAiModel(run.model),
    formatAiCost(run.usage.cost),
    formatAiDuration(run),
  ]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}

function canPublish(run: ExpertAiStepRun): boolean {
  return summaryKind.value !== null && run.status === "succeeded" && run.id !== props.currentRunId;
}
</script>

<template>
  <UiDialog v-if="open" v-model="open" size="xl" :title="title" :text="subtitle" secondary-action-label="Close" @secondary-action="open = false">
    <div class="flex flex-col gap-3">
      <UiInfoBox v-if="error" variant="error" title="Couldn't load revisions">
        <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetch()" />
      </UiInfoBox>

      <ul v-else-if="isLoading" class="flex flex-col gap-2">
        <li v-for="n in 3" :key="n" class="h-14 animate-pulse rounded-lg bg-se-gray-100" />
      </ul>

      <div
        v-else-if="runs.length === 0"
        class="flex flex-col items-center gap-2 rounded-lg border border-dashed border-se-gray-300 px-4 py-10 text-center"
      >
        <UiText as="p" variant="p-small" class="font-semibold text-se-dark" text="No revisions yet" />
        <UiText as="p" variant="caption-normal" class="text-se-gray-500" text="This step has not been run for this expert." />
      </div>

      <template v-else>
        <UiText as="p" variant="caption-normal" class="text-se-gray-500" :text="`${total} revision${total === 1 ? '' : 's'}`" />
        <ul class="flex flex-col divide-y divide-se-gray-200 rounded-lg border border-se-gray-200">
          <li v-for="run in runs" :key="run.id" class="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-4">
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <UiText as="span" variant="p-small" class="font-semibold text-se-dark" :text="`Revision ${run.revision}`" />
                <AiRunStatusChip :status="run.status" />
                <UiChip v-if="run.id === currentRunId" text="Live on profile" variant="success" size="xs" rounded="full" />
                <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="formatAiDateTime(run.finished_at ?? run.created_at)" />
              </div>
              <UiText as="p" variant="caption-normal" class="text-se-gray-600" :text="meta(run)" />
              <UiText v-if="run.error" as="p" variant="caption-normal" class="text-se-warn" :text="run.error" />
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              <UiStandardButton variant="ghost" size="sm" label="Open" @click="emit('view-run', run.id)" />
              <UiTooltip v-if="summaryKind && run.id !== currentRunId" content="Show this revision on the public profile">
                <UiStandardButton
                  variant="outline"
                  size="sm"
                  label="Make live"
                  :disabled="!canPublish(run) || isPublishing"
                  @click="emit('publish', { kind: summaryKind, runId: run.id })"
                />
              </UiTooltip>
            </div>
          </li>
        </ul>
        <div v-if="hasNextPage" class="flex justify-center">
          <UiStandardButton
            variant="outline"
            size="sm"
            :label="isFetchingNextPage ? 'Loading…' : 'Load older revisions'"
            :disabled="isFetchingNextPage"
            @click="() => fetchNextPage()"
          />
        </div>
      </template>
    </div>
  </UiDialog>
</template>
